import React, { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'i18n'
import { useUserNotifications } from '../hooks/use-user-notifications'
import { useMarkAsRead } from '../hooks/use-mark-as-read'
import type { UserNotification } from '../types'
import NotificationItem from './notification-item'

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

const APP_V2_PREFIX = '/app-v2'

const isAppV2Path = (path: string): boolean =>
  path === APP_V2_PREFIX || path.startsWith(`${APP_V2_PREFIX}/`)

interface NotificationDropdownProps {
  onClose: VoidFunction
  bellRef: React.RefObject<HTMLButtonElement>
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onClose,
  bellRef
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { notifications, isLoading, hasMore, fetchMore, isFetchingMore } =
    useUserNotifications()
  const {
    markAsRead,
    markAllAsRead,
    markAsClicked,
    dismissNotification,
    isMarkingAllAsRead
  } = useMarkAsRead()
  const scrollRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current?.contains(e.target as Node) ||
        bellRef.current?.contains(e.target as Node)
      )
        return
      onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose, bellRef])

  useEffect(() => {
    const dialog = dropdownRef.current

    if (!dialog) {
      return
    }

    const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)

    if (firstFocusable) {
      firstFocusable.focus()
    } else {
      dialog.setAttribute('tabindex', '-1')
      dialog.focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      )

      if (!focusables.length) {
        event.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (!active || !dialog.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
        return
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    const bellElement = bellRef.current

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      bellElement?.focus()
    }
  }, [onClose, bellRef])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || !hasMore || isFetchingMore) return
    const threshold = 40
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      fetchMore()
    }
  }, [hasMore, isFetchingMore, fetchMore])

  const handleNotificationClick = (notification: UserNotification) => {
    markAsClicked(notification.id)

    const link = (notification.data?.deep_link || notification.data?.url) as
      | string
      | undefined

    if (link) {
      if (link.startsWith('http') || isAppV2Path(link)) {
        window.location.href = link
      } else {
        history.push(link)
      }
      onClose()
    }
  }

  const handleMarkAsRead = (id: string) => {
    markAsRead(id)
  }

  const handleDismiss = (id: string) => {
    dismissNotification(id)
  }

  return (
    <>
      <div
        className="hidden max-[992px]:block fixed inset-0 bg-[rgba(5,59,75,0.7)] z-19"
        onClick={onClose}
      />
      <div
        ref={dropdownRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('Notificações')}
        className="absolute top-[58px] right-0 w-[360px] max-h-[440px] rounded-large border border-[#DEE1E6] bg-white shadow-down-medium z-20 flex flex-col overflow-hidden max-[992px]:fixed max-[992px]:top-auto max-[992px]:bottom-[56px] max-[992px]:left-0 max-[992px]:right-0 max-[992px]:w-full max-[992px]:max-h-[calc(70vh-56px)] max-[992px]:rounded-t-xlarge max-[992px]:rounded-b-none max-[992px]:border-b-0"
      >
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#F1F7FB]">
          <span className="text-[18px] font-bold text-[#053B4B]">
            {t('Notificações')}
          </span>
          <button
            onClick={() => markAllAsRead()}
            disabled={isMarkingAllAsRead}
            className="text-xs text-primary-600 font-bold bg-white border-none cursor-pointer py-1.5 rounded-lg transition-colors disabled:text-[#B7C7CB] disabled:cursor-not-allowed"
          >
            {t('Marcar todas como lidas')}
          </button>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          {isLoading && (
            <div className="flex items-center justify-center min-h-[390px] text-[#7C98A1] text-sm">
              {t('Carregando...')}
            </div>
          )}

          {!isLoading && notifications.length === 0 && (
            <div className="flex items-center justify-center min-h-[390px] text-[#7C98A1] text-sm">
              {t('Nenhuma notificação')}
            </div>
          )}

          {notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={handleMarkAsRead}
              onDismiss={handleDismiss}
              onClick={handleNotificationClick}
            />
          ))}

          {isFetchingMore && (
            <div className="flex items-center justify-center py-2.5 text-xs text-[#7C98A1]">
              {t('Carregando...')}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default NotificationDropdown
